/* eslint-disable max-len */
import { FC, memo, useState } from 'react';
import { FormikProps } from 'formik';
import { PhoneInput } from '../../../../components/PhoneInput/PhoneInput';
import {
	CardContent, CardActions, Divider, Grid,
	TextField, Typography, Button, Box, MenuItem,
	Alert,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import SendIcon from '@mui/icons-material/Send';
import { SelectState } from '@pm/shared';
import { InfoOutlined } from '@mui/icons-material';

interface Props {
	onComplete: () => void;
	onBack: () => void;
	formik: FormikProps<any>;
	pharmacyManagementSystems: string[];
}

export const IPCStep2: FC<Props> = memo(({ onComplete, onBack, formik, pharmacyManagementSystems }) => {
	const [isSubmitted] = useState(false);


	if (isSubmitted) {
		return (
			<Box>
				<CardContent>
					<Typography variant="h5" gutterBottom align="center">
						Registration Complete!
					</Typography>
					<Typography variant="body1" color="textSecondary" sx={{ mb: 3 }} align="center">
						What&apos;s Next? You will receive an email shortly to electronically sign the User Agreement and Business Associate Agreement.
					</Typography>
				</CardContent>
			</Box>
		);
	}

	return (
		<Box>
			<CardContent>
				<Alert 
					severity="warning" 
					sx={{ 
						mb: 3,
						backgroundColor: '#fff3cd',
						borderColor: '#ffeaa7',
						color: '#856404',
						'& .MuiAlert-icon': {
							display: 'none',
						},
					}}
				>
					<Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
						<InfoOutlined sx={{ color: '#856404', fontSize: 28, mr: 1 }} />
						<Typography variant="h6" sx={{ fontWeight: 'bold', color: '#856404' }}>
							Pharmacy Information
						</Typography>
					</Box>
					<Typography variant="body2" sx={{ color: '#856404' }}>
						The following information will be used to create your Pharmacy Marketplace account.
					</Typography>
				</Alert>
				<Typography variant="h5" gutterBottom>
					Register Pharmacy
				</Typography>
				<Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
					Please provide your pharmacy information to complete the registration.
				</Typography>
				
				{/* Row 1: Company Legal Name, Pharmacy DBA Name, Pharmacy Phone Number, Pharmacy Email */}
				<Grid container spacing={2} sx={{ mb: 2 }}>
					<Grid item md={3} xs={12}>
						<TextField
							fullWidth
							required
							name='companyLegalName'
							label='Company Legal Name'
							value={formik.values.companyLegalName}
							onChange={formik.handleChange}
							onBlur={formik.handleBlur}
							error={formik.touched.companyLegalName && Boolean(formik.errors.companyLegalName)}
							helperText={(formik.touched.companyLegalName && formik.errors.companyLegalName as string) || ' '}
						/>
					</Grid>
					<Grid item md={3} xs={12}>
						<TextField
							fullWidth
							required
							name='pharmacyDbaName'
							label='Pharmacy DBA Name'
							value={formik.values.pharmacyDbaName}
							onChange={formik.handleChange}
							onBlur={formik.handleBlur}
							error={formik.touched.pharmacyDbaName && Boolean(formik.errors.pharmacyDbaName)}
							helperText={(formik.touched.pharmacyDbaName && formik.errors.pharmacyDbaName as string) || ' '}
						/>
					</Grid>
					<Grid item md={3} xs={12}>
						<TextField
							fullWidth
							required
							name='pharmacyPhoneNumber'
							label='Pharmacy Phone Number'
							placeholder='(123) 456-7890'
							value={formik.values.pharmacyPhoneNumber}
							onChange={formik.handleChange}
							onBlur={formik.handleBlur}
							error={formik.touched.pharmacyPhoneNumber && Boolean(formik.errors.pharmacyPhoneNumber)}
							helperText={(formik.touched.pharmacyPhoneNumber && formik.errors.pharmacyPhoneNumber as string) || ' '}
							InputProps={{
								inputComponent: PhoneInput as any,
							}}
						/>
					</Grid>
					<Grid item md={3} xs={12}>
						<TextField
							fullWidth
							required
							name='pharmacyEmail'
							label='Pharmacy Email'
							type='email'
							value={formik.values.pharmacyEmail}
							onChange={formik.handleChange}
							onBlur={formik.handleBlur}
							error={formik.touched.pharmacyEmail && Boolean(formik.errors.pharmacyEmail)}
							helperText={(formik.touched.pharmacyEmail && formik.errors.pharmacyEmail as string) || ' '}
						/>
					</Grid>
				</Grid>

				{/* Row 2: Street Address, City, State, Zip Code */}
				<Grid container spacing={2} sx={{ mb: 2 }}>
					<Grid item md={3} xs={12}>
						<TextField
							fullWidth
							required
							name='streetAddress'
							label='Street Address'
							value={formik.values.streetAddress}
							onChange={formik.handleChange}
							onBlur={formik.handleBlur}
							error={formik.touched.streetAddress && Boolean(formik.errors.streetAddress)}
							helperText={(formik.touched.streetAddress && formik.errors.streetAddress as string) || ' '}
						/>
					</Grid>
					<Grid item md={3} xs={12}>
						<TextField
							fullWidth
							required
							name='city'
							label='City'
							value={formik.values.city}
							onChange={formik.handleChange}
							onBlur={formik.handleBlur}
							error={formik.touched.city && Boolean(formik.errors.city)}
							helperText={(formik.touched.city && formik.errors.city as string) || ' '}
						/>
					</Grid>
					<Grid item md={3} xs={12}>
						<SelectState
							name='state'
							label='State'
							required
							value={formik.values.state}
							onChange={formik.handleChange}
							onBlur={formik.handleBlur}
							error={formik.touched.state && Boolean(formik.errors.state)}
							helperText={(formik.touched.state && formik.errors.state as string) || ' '}
						/>
					</Grid>
					<Grid item md={3} xs={12}>
						<TextField
							fullWidth
							required
							name='zipCode'
							label='Zip Code'
							value={formik.values.zipCode}
							onChange={formik.handleChange}
							onBlur={formik.handleBlur}
							error={formik.touched.zipCode && Boolean(formik.errors.zipCode)}
							helperText={(formik.touched.zipCode && formik.errors.zipCode as string) || ' '}
						/>
					</Grid>
				</Grid>

				{/* Row 3: DEA, NCPDP, State Pharmacy License, Pharmacy Management System */}
				<Grid container spacing={2} sx={{ mb: 2 }}>
					<Grid item md={3} xs={12}>
						<TextField
							fullWidth
							required
							name='dea'
							label='DEA'
							value={formik.values.dea}
							onChange={formik.handleChange}
							onBlur={formik.handleBlur}
							error={formik.touched.dea && Boolean(formik.errors.dea)}
							helperText={(formik.touched.dea && formik.errors.dea as string) || ' '}
							inputProps={{ style: { textTransform: 'uppercase' } }}
						/>
					</Grid>
					<Grid item md={3} xs={12}>
						<TextField
							fullWidth
							name='ncpdp'
							label='NCPDP'
							value={formik.values.ncpdp}
							onChange={formik.handleChange}
							onBlur={formik.handleBlur}
							error={formik.touched.ncpdp && Boolean(formik.errors.ncpdp)}
							helperText={(formik.touched.ncpdp && formik.errors.ncpdp as string) || ' '}
						/>
					</Grid>
					<Grid item md={3} xs={12}>
						<TextField
							fullWidth
							name='statePharmacyLicense'
							label='State Pharmacy License'
							value={formik.values.statePharmacyLicense}
							onChange={formik.handleChange}
							onBlur={formik.handleBlur}
							error={formik.touched.statePharmacyLicense && Boolean(formik.errors.statePharmacyLicense)}
							helperText={(formik.touched.statePharmacyLicense && formik.errors.statePharmacyLicense as string) || ' '}
						/>
					</Grid>
					<Grid item md={3} xs={12}>
						<TextField
							fullWidth
							select
							required
							name='pharmacyManagementSystem'
							label='Pharmacy Management System'
							value={formik.values.pharmacyManagementSystem}
							onChange={formik.handleChange}
							onBlur={formik.handleBlur}
							error={formik.touched.pharmacyManagementSystem && Boolean(formik.errors.pharmacyManagementSystem)}
							helperText={(formik.touched.pharmacyManagementSystem && formik.errors.pharmacyManagementSystem as string) || ' '}
						>
							{pharmacyManagementSystems.map((system) => (
								<MenuItem key={system} value={system}>
									{system}
								</MenuItem>
							))}
						</TextField>
					</Grid>
				</Grid>

				{/* Row 4: Primary Account Number, IPC Account Number */}
				<Grid container spacing={2} sx={{ mb: 2 }}>
					<Grid item md={6} xs={12}>
						<TextField
							fullWidth
							required
							name='primaryAccountNumber'
							label='Primary Account Number'
							value={formik.values.primaryAccountNumber}
							onChange={formik.handleChange}
							onBlur={formik.handleBlur}
							error={formik.touched.primaryAccountNumber && Boolean(formik.errors.primaryAccountNumber)}
							helperText={(formik.touched.primaryAccountNumber && formik.errors.primaryAccountNumber as string) || ' '}
						/>
					</Grid>
					<Grid item md={6} xs={12}>
						<TextField
							fullWidth
							required
							name='ipcAccountNumber'
							label='IPC Account Number'
							value={formik.values.ipcAccountNumber}
							onChange={formik.handleChange}
							onBlur={formik.handleBlur}
							error={formik.touched.ipcAccountNumber && Boolean(formik.errors.ipcAccountNumber)}
							helperText={(formik.touched.ipcAccountNumber && formik.errors.ipcAccountNumber as string) || ' '}
						/>
					</Grid>
				</Grid>
			</CardContent>
			<Divider sx={{ marginBottom: 1 }} />
			<CardActions>
				<Grid 
					container 
					spacing={2} 
					alignItems='center'
					sx={{ px: 1 }}
				>
					<Grid item xs={12} md={6}>
						<Button
							variant='outlined'
							onClick={onBack}
						>
							Back
						</Button>
					</Grid>
					<Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
						<LoadingButton
							type='submit'
							loading={formik.isSubmitting}
							loadingPosition='start'
							color='primary'
							variant='contained'
							startIcon={<SendIcon />}
							onClick={onComplete}
						>
							Submit
						</LoadingButton>
					</Grid>
				</Grid>
			</CardActions>
		</Box>
	);
}); 